import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
} from "react";
import { Drawer, Button, message, Modal, Card, List, Spin, Select } from "antd";
import { ShoppingBag, Trash, User, Calendar, Layers } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useKeyboardShortcuts } from "../../../Hook";
import { SHORTCUTS } from "../../../Constant";
import {
  useDraftCartsListQuery,
  useActiveOrderStatusesQuery,
  useSaveDraftCartMutation,
  useDeleteDraftCartMutation,
  useUpdateDraftCartStatusMutation,
} from "../../../Services";
import { useCart, useAuthChecker } from "../../../Context";
import { CartDraftNamingModal } from "./CartDraftNamingModal";
import { sendKOTPrintJob } from "../../../lib";
import { drawerTitle } from "../../../components";

export interface DraftOptionsRef {
  openDraftList: () => void;
  handleSaveDraft: () => void;
}

interface DraftOptionsProps {
  onCloseQuickAction: () => void;
  onSavePendingChange?: (isPending: boolean) => void;
  refetchTables: () => void;
}

export const DraftOptions = forwardRef<DraftOptionsRef, DraftOptionsProps>(
  ({ onCloseQuickAction, onSavePendingChange, refetchTables }, ref) => {
    const [isDraftListOpen, setIsDraftListOpen] = useState(false);
    const [namingModalOpen, setNamingModalOpen] = useState(false);
    const [draftName, setDraftName] = useState("");
    const [selectedDraftIndex, setSelectedDraftIndex] = useState<number | null>(
      null,
    );
    const [updatingStatusId, setUpdatingStatusId] = useState<
      string | number | null
    >(null);

    useAuthChecker();
    const [isPrinting, setIsPrinting] = useState(false);

    const {
      customer,
      setCustomer,
      table,
      setTable,
      cart,
      setCart,
      clearCart,
      editingDraftId,
      setEditingDraftId,
      addEditingDraftObj,
      originalDraftItems,
      setOriginalDraftItems,
    } = useCart();

    const queryClient = useQueryClient();

    const { data: draftCartsData, isLoading: draftsLoading } =
      useDraftCartsListQuery({
        enabled: isDraftListOpen,
      });
    const draftCarts = draftCartsData || [];

    const { data: orderStatuses = [] } = useActiveOrderStatusesQuery({
      enabled: isDraftListOpen,
    });

    const updateStatusMutation = useUpdateDraftCartStatusMutation();

    const handleStatusChange = (
      id: string | number,
      orderStatusId: string | null,
    ) => {
      setUpdatingStatusId(id);
      updateStatusMutation.mutate(
        { id, order_status_id: orderStatusId },
        {
          onSuccess: (res) => {
            if (res?.success) {
              message.success("Order status updated successfully.");
              refetchTables();
            } else {
              message.error(res?.message || "Failed to update order status.");
            }
          },
          onError: () => {
            message.error("Failed to update order status.");
          },
          onSettled: () => {
            setUpdatingStatusId(null);
          },
        },
      );
      setCart([]);
      setCustomer(null);
      setTable(null);
      setEditingDraftId(null);
    };

    useEffect(() => {
      if (isDraftListOpen && draftCarts.length > 0) {
        setSelectedDraftIndex(0);
      } else {
        setSelectedDraftIndex(null);
      }
    }, [isDraftListOpen, draftCarts]);

    useKeyboardShortcuts(
      [
        {
          shortcut: SHORTCUTS.DRAFTS.NAVIGATE_UP,
          options: {
            enabled:
              isDraftListOpen &&
              draftCarts.length > 0 &&
              selectedDraftIndex !== null,
            preventDefault: true,
            stopPropagation: true,
          },
          handler: () => {
            setSelectedDraftIndex((prev) => {
              if (prev === null || draftCarts.length === 0) return 0;
              return (prev - 1 + draftCarts.length) % draftCarts.length;
            });
          },
        },
        {
          shortcut: SHORTCUTS.DRAFTS.NAVIGATE_DOWN,
          options: {
            enabled:
              isDraftListOpen &&
              draftCarts.length > 0 &&
              selectedDraftIndex !== null,
            preventDefault: true,
            stopPropagation: true,
          },
          handler: () => {
            setSelectedDraftIndex((prev) => {
              if (prev === null || draftCarts.length === 0) return 0;
              return (prev + 1) % draftCarts.length;
            });
          },
        },
        {
          shortcut: SHORTCUTS.DRAFTS.RESTORE,
          options: {
            enabled:
              isDraftListOpen &&
              draftCarts.length > 0 &&
              selectedDraftIndex !== null,
            preventDefault: true,
            stopPropagation: true,
          },
          handler: () => {
            if (selectedDraftIndex !== null && draftCarts[selectedDraftIndex]) {
              handleLoadDraftItem(draftCarts[selectedDraftIndex]);
            }
          },
        },
        {
          shortcut: SHORTCUTS.POS.SAVE_DRAFT,
          options: {
            enabled: cart.length > 0,
            preventDefault: true,
            stopPropagation: true,
          },
          handler: () => {
            handleDirectSaveDraft();
          },
        },
      ],
      { capture: true, stopImmediatePropagation: true },
    );

    const saveMutation = useSaveDraftCartMutation();

    useEffect(() => {
      if (onSavePendingChange) {
        onSavePendingChange(saveMutation.isPending || isPrinting);
      }
    }, [saveMutation.isPending, isPrinting, onSavePendingChange]);

    const deleteDraftMutation = useDeleteDraftCartMutation();

    const buildKOTDiff = (oldCart = [], newCart = []) => {
      const oldMap = new Map(oldCart.map((i) => [i.id, i]));
      const newMap = new Map(newCart.map((i) => [i.id, i]));

      const printerGroups = {};

      const addItemToPrinter = (ip, item) => {
        if (!printerGroups[ip]) {
          printerGroups[ip] = [];
        }
        printerGroups[ip].push(item);
      };

      // ----------------------------
      // 1. Handle NEW / UPDATED items
      // ----------------------------
      for (const item of newCart) {
        const ip = item?.rawProduct?.ip_address;
        if (!ip) continue;

        const old = oldMap.get(item.id);

        const oldQty = old?.printed_qty ?? 0;
        const newQty = item.qty;

        // ADDED / INCREASED
        if (newQty > oldQty) {
          addItemToPrinter(ip, {
            name: item.name,
            quantity: newQty - oldQty,
            special_note: item.description,
            status: "ORDERED",
            is_canceled: false,
            is_replacement: false,
          });
        }

        // REDUCED (CANCEL)
        if (newQty < oldQty) {
          addItemToPrinter(ip, {
            name: item.name,
            quantity: oldQty - newQty,
            special_note: item.description,
            status: "CANCEL",
            is_canceled: true,
            is_replacement: false,
          });
        }
      }

      // ----------------------------
      // 2. Handle REMOVED items
      // ----------------------------
      for (const oldItem of oldCart) {
        if (newMap.has(oldItem.id)) continue;

        const ip = oldItem?.rawProduct?.ip_address;
        if (!ip) continue;

        const qty = oldItem.printed_qty ?? oldItem.qty;

        addItemToPrinter(ip, {
          name: oldItem.name,
          quantity: qty,
          special_note: oldItem.description,
          status: "CANCEL",
          is_canceled: true,
          is_replacement: false,
        });
      }

      return printerGroups;
    };

    const handleSaveDraftWithPrinting = async (nameToUse) => {
      setIsPrinting(true);

      try {
        const oldCart = originalDraftItems || [];
        const newCart = cart;
        const printerGroups = buildKOTDiff(oldCart, newCart);

        const printerEntries = Object.entries(printerGroups);

        let printed = false;

        if (printerEntries.length > 0) {
          const printResults = await Promise.all(
            printerEntries.map(async ([ip, menu_items]) => {
              try {
                return await sendKOTPrintJob({
                  printer_ip: ip,
                  order_data: {
                    date: new Date().toISOString().split("T")[0],
                    order_id: "Draft - " + draftName,
                    waiter_name: "waiter",
                    order_type: table?.id ? "Dine-in" : "Take-away",
                    section: "Main",
                    table: table?.name ?? "Takeaway",
                    status: editingDraftId ? "UPDATE ORDER" : "NEW ORDER",
                    menu_items,
                  },
                });
              } catch (err) {
                console.error(`Printer ${ip} failed`, err);
                return false;
              }
            }),
          );

          printed = printResults.some(Boolean);
        }

        saveMutation.mutate(
          {
            id: editingDraftId
              ? Number(editingDraftId) || editingDraftId
              : null,
            customer_id: customer?.id || null,
            table_id: table?.id || null,
            draft_name: nameToUse,
            cart_data: newCart.map((i) => ({
              ...i,
              printed_qty: i.qty,
              is_print: !!i?.rawProduct?.ip_address,
            })),
          },
          {
            onSuccess: (res) => {
              if (res?.success) {
                message.success("Draft saved successfully.");
                onCloseQuickAction();
                clearCart();
                setOriginalDraftItems([]);
                refetchTables();
              } else {
                message.error(res?.message || "Failed to save draft.");
              }
            },
            onError: () => {
              message.error("Failed to save draft.");
            },
          },
        );
      } catch (err) {
        console.error(err);
        message.error("An error occurred while saving the draft.");
      } finally {
        setIsPrinting(false);
      }
    };

    const handleSaveDraft = () => {
      if (cart.length === 0) {
        message.warning("Cart is empty. Nothing to save.");
        return;
      }

      let initialName = "Walk-in Order";
      if (customer) {
        initialName = customer.name;
      } else if (table) {
        initialName = table.name;
      }

      if (editingDraftId) {
        handleSaveDraftWithPrinting(draftName || initialName);
      } else {
        setDraftName(initialName);
        setNamingModalOpen(true);
      }
    };

    const handleDirectSaveDraft = () => {
      if (cart.length === 0) {
        message.warning("Cart is empty. Nothing to save.");
        return;
      }

      let nameToUse = draftName;
      if (!nameToUse) {
        if (customer) {
          nameToUse = customer.name;
        } else if (table) {
          nameToUse = table.name;
        } else {
          nameToUse = "Walk-in Order";
        }
      }

      handleSaveDraftWithPrinting(nameToUse);
    };

    const handleLoadDraftItem = async (draft: any) => {
      clearCart();
      if (draft.customer_id) {
        setCustomer({
          id: draft.customer_id,
          name: draft.customer_name,
          credit_limit: draft?.credit_limit,
        });
      } else {
        setCustomer(null);
      }
      if (draft.table_id) {
        setTable({ id: draft.table_id, name: draft.table_name });
      } else {
        setTable(null);
      }
      const cartData = draft.cart_data || [];
      setCart(cartData);
      setEditingDraftId(draft.id);
      setDraftName(draft.draft_name || "");
      setOriginalDraftItems(cartData);
      addEditingDraftObj(draft);

      queryClient.invalidateQueries({ queryKey: ["pos-draft-carts"] });
      refetchTables();

      message.success("Draft cart restored successfully.");
      setIsDraftListOpen(false);
      onCloseQuickAction();
    };

    const handleDeleteDraftItem = (draft: any, e: React.MouseEvent) => {
      e.stopPropagation();
      Modal.confirm({
        title: "Delete Saved Draft?",
        content:
          "Are you sure you want to permanently delete this saved draft cart? This will print cancellation KOTs for any printed items.",
        okText: "Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          deleteDraftMutation.mutate(
            { id: draft.id },
            {
              onSuccess: (res) => {
                if (res?.success) {
                  message.success("Draft deleted successfully.");
                  refetchTables();
                  if (editingDraftId === String(draft.id)) {
                    setEditingDraftId(null);
                    setOriginalDraftItems([]);
                  }
                } else {
                  message.error(res?.message || "Failed to delete draft.");
                }
              },
              onError: () => {
                message.error("Failed to delete draft.");
              },
            },
          );
        },
      });
    };

    useImperativeHandle(ref, () => ({
      openDraftList: () => setIsDraftListOpen(true),
      handleSaveDraft,
    }));

    return (
      <>
        <CartDraftNamingModal
          open={namingModalOpen}
          onCancel={() => setNamingModalOpen(false)}
          draftName={draftName}
          setDraftName={setDraftName}
          onOk={() => {
            setNamingModalOpen(false);
            handleSaveDraftWithPrinting(draftName.trim());
          }}
        />

        {/* Saved Drafts List Drawer */}
        <Drawer
          title={drawerTitle("Saved Draft Carts")}
          extra={
            <span className="text-xs bg-emerald-50 text-emerald-600 font-bold px-2 py-0.5 rounded-full">
              {draftCarts.length} Drafts
            </span>
          }
          placement="right"
          width={450}
          onClose={() => setIsDraftListOpen(false)}
          open={isDraftListOpen}
          style={{ zIndex: 1001 }}
        >
          {draftsLoading ? (
            <div className="flex items-center justify-center h-64">
              <Spin size="large" />
            </div>
          ) : draftCarts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 mb-4">
                <ShoppingBag className="w-6 h-6" />
              </div>
              <h3 className="font-semibold text-gray-800 text-xs">
                No saved drafts found
              </h3>
              <p className="text-[10px] text-gray-400 mt-1 max-w-[180px]">
                Save draft carts from the actions menu to retrieve them later.
              </p>
            </div>
          ) : (
            <List
              dataSource={draftCarts}
              className="flex flex-col gap-2"
              renderItem={(draft: any, index: number) => {
                const draftTotal =
                  draft.cart_data?.reduce(
                    (acc: number, item: any) => acc + item.price * item.qty,
                    0,
                  ) || 0;
                const isFocused = selectedDraftIndex === index;

                return (
                  <List.Item className="p-0 border-none mb-3">
                    <Card
                      hoverable
                      onClick={() => handleLoadDraftItem(draft)}
                      className={`w-full transition-all border rounded-xl overflow-hidden ${
                        isFocused
                          ? "border-indigo-500 bg-indigo-50/10 ring-2 ring-indigo-500/10 shadow-sm"
                          : "border-gray-200/80 hover:border-indigo-300"
                      }`}
                      bodyStyle={{ padding: "14px" }}
                    >
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <ShoppingBag className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                            <span className="font-bold text-xs text-gray-900 truncate">
                              {draft.draft_name || `Draft #${draft.id}`}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              type="text"
                              size="small"
                              danger
                              className="hover:bg-red-50 flex items-center justify-center h-6 w-6 rounded-lg p-0"
                              onClick={(e) => handleDeleteDraftItem(draft, e)}
                            >
                              <Trash className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 text-[10px] text-gray-500 font-medium">
                          <User className="w-3 h-3 text-gray-400 shrink-0" />
                          <span>
                            Customer: {draft.customer_name || "Walk-in"}
                          </span>
                          {draft.customer_phone && (
                            <span className="text-gray-300">|</span>
                          )}
                          {draft.customer_phone && (
                            <span>{draft.customer_phone}</span>
                          )}
                        </div>

                        {draft.table_name && (
                          <div className="flex items-center gap-1.5 text-[10px] text-gray-500 font-medium mt-0.5">
                            <Layers className="w-3 h-3 text-gray-400 shrink-0" />
                            <span>Table: {draft.table_name}</span>
                          </div>
                        )}

                        <div
                          className="flex items-center justify-between gap-1.5 text-[10px] text-gray-500 font-medium mt-1.5"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="flex items-center gap-1.5 text-[10px] text-gray-500 font-medium">
                            <Layers className="w-3 h-3 text-gray-400 shrink-0" />
                            <span>Status:</span>
                          </span>
                          <Select
                            size="small"
                            placeholder="Set Status"
                            style={{ width: 140 }}
                            value={draft.order_status_id || undefined}
                            onChange={(value) =>
                              handleStatusChange(draft.id, value || null)
                            }
                            loading={updatingStatusId === draft.id}
                            disabled={updatingStatusId === draft.id}
                            allowClear
                            dropdownStyle={{ zIndex: 1002 }}
                          >
                            {orderStatuses.map((status: any) => (
                              <Select.Option key={status.id} value={status.id}>
                                {status.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </div>

                        <div className="flex items-center justify-between border-t border-gray-100/80 pt-2.5 mt-1 text-[10px] text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3 text-gray-400" />
                            {new Date(draft.updated_at).toLocaleString(
                              undefined,
                              {
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                          </span>
                          <span className="flex items-center gap-2">
                            <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded text-[9px] font-semibold">
                              {draft.cart_data?.length || 0} items
                            </span>
                            <span className="font-black text-xs text-indigo-600">
                              LKR {draftTotal.toLocaleString()}
                            </span>
                          </span>
                        </div>
                      </div>
                    </Card>
                  </List.Item>
                );
              }}
            />
          )}
        </Drawer>
      </>
    );
  },
);
